import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card, CardContent } from './ui/card';
import {
  Building2, MapPin, Building, CheckCircle, Settings,
  Stethoscope, Shield, CreditCard, Activity, Navigation,
  GraduationCap, FileText, Tags, Users, XCircle, AlertCircle, DollarSign, Truck
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { hasGranularPermission } from '../lib/permissions';
import TiposDocumentosPage from './TiposDocumentosPage';
import TiposDemissaoPage from './TiposDemissaoPage';
import EtniasPage from './EtniasPage';
import GrausHierarquiaPage from './GrausHierarquiaPage';
import JustificativasFaltaPage from './JustificativasFaltaPage';
import ClientesPage from './ClientesPage';
import Escolaridade from './cadastros/Escolaridade';
import ContractTypesPage from './ContractTypesPage';
import CrudSection from './cadastros/CrudSection';
import CompanySection from './CompanySection';
import AdiantamentoSalarialSection from './cadastros/AdiantamentoSalarialSection';

const CADASTRO_TABS = [
  { id: 'cadastros_empresas',             label: 'Empresas',                icon: Building2 },
  { id: 'cadastros_adiantamento_salarial', label: 'Adiantamento Salarial', icon: DollarSign },
  { id: 'cadastros_filiais',              label: 'Filiais',                icon: MapPin },
  { id: 'cadastros_departamentos',        label: 'Departamentos',        icon: Building },
  { id: 'cadastros_situacoes',            label: 'Situações',            icon: CheckCircle },
  { id: 'cadastros_tipos_documentos',     label: 'Tipos de Documentos',  icon: FileText },
  { id: 'cadastros_tipos_demissao',       label: 'Tipos de Demissão',    icon: XCircle },
  { id: 'cadastros_etnias',               label: 'Etnias',               icon: Users },
  { id: 'cadastros_graus_hierarquia',     label: 'Graus de Hierarquia',  icon: Settings },
  { id: 'cadastros_odonto',               label: 'Odonto',               icon: Stethoscope },
  { id: 'cadastros_seg_vida',             label: 'Seg. Vida',            icon: Shield },
  { id: 'cadastros_total_pass',           label: 'Total Pass',           icon: CreditCard },
  { id: 'cadastros_med_ocupacional',      label: 'Med. Ocupacional',     icon: Activity },
  { id: 'cadastros_enderecos',            label: 'Endereços',            icon: Navigation },
  { id: 'cadastros_escolaridade',         label: 'Escolaridade',         icon: GraduationCap },
  { id: 'cadastros_tipos_contrato',       label: 'Tipos de Contrato',    icon: Tags },
  { id: 'cadastros_justificativas_falta', label: 'Justificativas de Falta', icon: AlertCircle },
  { id: 'cadastros_clientes',             label: 'Clientes',              icon: Building2 },
  { id: 'cadastros_transporte',           label: 'Transporte',             icon: Truck },
  { id: 'cadastros_outros',               label: 'Outros Cadastros',     icon: Settings },
];

const CRUD_TABS = [
  'cadastros_filiais',
  'cadastros_departamentos', 'cadastros_situacoes', 'cadastros_odonto',
  'cadastros_seg_vida', 'cadastros_total_pass', 'cadastros_med_ocupacional',
  'cadastros_enderecos', 'cadastros_transporte',
];

const SPECIALIZED_TABS = {
  'cadastros_empresas':               () => <CompanySection />,
  'cadastros_adiantamento_salarial':  () => <AdiantamentoSalarialSection />,
  'cadastros_tipos_documentos':     () => <TiposDocumentosPage />,
  'cadastros_tipos_demissao':       () => <TiposDemissaoPage />,
  'cadastros_etnias':               () => <EtniasPage />,
  'cadastros_graus_hierarquia':     () => <GrausHierarquiaPage />,
  'cadastros_escolaridade':         () => <Escolaridade />,
  'cadastros_tipos_contrato':       () => <ContractTypesPage />,
  'cadastros_justificativas_falta': () => <JustificativasFaltaPage />,
  'cadastros_clientes':           () => <ClientesPage />,
  'cadastros_outros':               () => (
    <Card>
      <CardContent className="py-12 text-center text-slate-500">
        <Settings className="h-12 w-12 mx-auto mb-4 text-slate-300" />
        <p className="text-lg font-medium">Outros Cadastros</p>
        <p className="text-sm mt-1">Categorias adicionais serão exibidas aqui.</p>
      </CardContent>
    </Card>
  ),
};

const CadastrosPage = () => {
  const { user } = useAuth();
  const location = useLocation();

  const tabsPermitidos = useMemo(() =>
    CADASTRO_TABS.filter(t =>
      !user?.permitted_ids?.length || user.is_admin ||
      user.permitted_ids.includes(t.id) ||
      hasGranularPermission(user, 'cadastros', t.id)
    ),
    [user]
  );

  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(location.search);
    const tabFromUrl = params.get('tab');
    if (tabFromUrl && tabsPermitidos.some((t) => t.id === tabFromUrl)) return tabFromUrl;
    return tabsPermitidos.length > 0 ? tabsPermitidos[0].id : 'cadastros_empresas';
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabFromUrl = params.get('tab');
    if (tabFromUrl && tabsPermitidos.some((t) => t.id === tabFromUrl)) {
      setActiveTab(tabFromUrl);
    }
  }, [location.search, user]);

  const handleTabChange = useCallback((value) => {
    setActiveTab(value);
  }, []);

  const currentTab = tabsPermitidos.find((t) => t.id === activeTab);
  const Icon = currentTab?.icon || Building2;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Cadastros</h1>
          <p className="text-slate-600 mt-1">
            Gerencie empresas, filiais, cargos, departamentos e outros cadastros do sistema
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <Card>
          <CardContent className="pt-6">
            <TabsList className="flex flex-wrap h-auto gap-1 bg-transparent">
              {tabsPermitidos.map((tab) => {
                const TabIcon = tab.icon;
                return (
                  <TabsTrigger
                    key={tab.id}
                    value={tab.id}
                    className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                  >
                    <TabIcon className="h-4 w-4 mr-1.5" />
                    {tab.label}
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </CardContent>
        </Card>

        {tabsPermitidos.map((tab) => (
          <TabsContent key={tab.id} value={tab.id} className="mt-6">
            <div className="flex items-center gap-2 mb-4">
              <Icon className="h-6 w-6 text-primary" />
              <h2 className="text-2xl font-bold">{currentTab?.label}</h2>
            </div>
            {CRUD_TABS.includes(tab.id) ? (
              <CrudSection tabId={tab.id} />
            ) : (
              SPECIALIZED_TABS[tab.id]?.()
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export default CadastrosPage;
